<script>
export default {
  name: "deleteAccountModal",
  props: {
    accountName: {
      required: true,
      type: String,
    },
  },
  data() {
    return {
      accountNameConfirmation1: "",
      accountNameConfirmation2: "",
    };
  },
  computed: {
    isConfirmationValid() {
      return (
        this.accountNameConfirmation1 === this.accountName &&
        this.accountNameConfirmation2 === this.accountName &&
        this.accountNameConfirmation1.length > 0
      );
    },
  },
  methods: {
    openModal() {
      this.accountNameConfirmation1 = "";
      this.accountNameConfirmation2 = "";
      this.$bvModal.show("deleteAccountModal");
    },
    close() {
      this.accountNameConfirmation1 = "";
      this.accountNameConfirmation2 = "";
      this.$bvModal.hide("deleteAccountModal");
    },
    confirm() {
      if (this.isConfirmationValid) {
        this.$root.$emit("account::delete::confirmation");
        this.close();
      }
    },
  },
};
</script>

<template>
  <div>
    <b-modal id="deleteAccountModal" size="lg" hide-header-close no-close-on-backdrop>
      <template v-slot:modal-header>
        <alertHeader/>
        <div class="w-100">
          <h4 class="modal-title">Delete Account</h4>
        </div>
      </template>

      <div class="modal-body-content">
        <div class="warning-section">
          <div class="warning-icon">⚠️</div>
          <div class="warning-text">
            <strong>This action cannot be undone.</strong>
          </div>
        </div>

        <div class="description-section">
          <p class="description-paragraph">
            Deleting your account will permanently remove:
          </p>
          <ul class="deletion-list">
            <li>All user accounts and permissions</li>
            <li>All proofs and files</li>
            <li>All workflows and settings</li>
            <li>All account data and history</li>
          </ul>
          <p class="description-paragraph">
            <strong>This action is immediate and irreversible.</strong> All data will be permanently deleted and cannot be recovered.
          </p>
        </div>

        <div class="confirmation-section">
          <p class="confirmation-instructions">
            To confirm deletion, please type your account name <strong class="account-name-highlight">{{ accountName }}</strong> twice below:
          </p>

          <div class="form-group">
            <label class="input-label">Account Name (1st confirmation)</label>
            <input
              type="text"
              class="form-control confirmation-input"
              v-model="accountNameConfirmation1"
              placeholder="Type account name"
              autocomplete="off"
            />
          </div>

          <div class="form-group">
            <label class="input-label">Account Name (2nd confirmation)</label>
            <input
              type="text"
              class="form-control confirmation-input"
              v-model="accountNameConfirmation2"
              placeholder="Type account name again"
              autocomplete="off"
            />
          </div>
        </div>
      </div>

      <template v-slot:modal-footer>
        <div class="w-100 d-flex justify-content-end align-items-center">
          <b-button class="gray-btn" @click="close()">
            Cancel
          </b-button>
          <b-button
            class="delete-btn ml-3"
            :disabled="!isConfirmationValid"
            @click="confirm()"
          >
            Delete Account Forever
          </b-button>
        </div>
      </template>
    </b-modal>
  </div>
</template>

<style scoped>
.modal-title {
  font-family: 'Open Sans';
  font-weight: 600;
  font-size: 20px;
  line-height: 28px;
  color: #CC333F;
  margin: 0;
}

.modal-body-content {
  padding: 0;
}

.warning-section {
  display: flex;
  align-items: center;
  padding: 16px;
  background: #FFF4E5;
  border: 1px solid #FFB020;
  border-radius: 6px;
  margin-bottom: 24px;
}

.warning-icon {
  font-size: 24px;
  margin-right: 12px;
}

.warning-text {
  font-family: 'Open Sans';
  font-size: 14px;
  line-height: 20px;
  color: #1B1B1C;
}

.description-section {
  margin-bottom: 24px;
}

.description-paragraph {
  font-family: 'Open Sans';
  font-size: 14px;
  line-height: 20px;
  color: #3E3E40;
  margin-bottom: 12px;
}

.deletion-list {
  font-family: 'Open Sans';
  font-size: 14px;
  line-height: 24px;
  color: #3E3E40;
  margin-left: 20px;
  margin-bottom: 16px;
}

.deletion-list li {
  margin-bottom: 4px;
}

.confirmation-section {
  border-top: 1px solid #E5E5E6;
  padding-top: 24px;
}

.confirmation-instructions {
  font-family: 'Open Sans';
  font-size: 14px;
  line-height: 20px;
  color: #3E3E40;
  margin-bottom: 16px;
}

.account-name-highlight {
  color: #CC333F;
  font-weight: 600;
}

.input-label {
  font-family: 'Open Sans';
  font-weight: 600;
  font-size: 13px;
  line-height: 20px;
  color: #575759;
  display: block;
  margin-bottom: 6px;
}

.confirmation-input {
  font-family: 'Open Sans';
  font-size: 14px;
  line-height: 20px;
  color: #1B1B1C;
  padding: 9px 13px;
  height: 38px;
  border: 1px solid #D1D1D3;
  border-radius: 6px;
  box-shadow: 0px 1px 2px rgba(8, 11, 13, 0.05);
  transition: border-color 0.15s ease-in-out, box-shadow 0.15s ease-in-out;
}

.confirmation-input:focus {
  border-color: #019256;
  outline: 0;
  box-shadow: 0 0 0 0.2rem rgba(1, 146, 86, 0.25);
}

.form-group {
  margin-bottom: 16px;
}

.gray-btn {
  font-family: 'Open Sans';
  font-weight: 400;
  font-size: 14px;
  line-height: 20px;
  color: #444444;
  display: flex;
  flex-direction: row;
  justify-content: center;
  align-items: center;
  padding: 9px 17px;
  height: 38px;
  background: #F4F4F6;
  border-radius: 6px;
  cursor: pointer;
}

.gray-btn:hover {
  background: #E5E5E6;
}

.delete-btn {
  font-family: 'Open Sans';
  font-weight: 600;
  font-size: 14px;
  line-height: 20px;
  display: flex;
  flex-direction: row;
  justify-content: center;
  align-items: center;
  padding: 9px 17px;
  height: 38px;
  background: #CC333F;
  border: 1px solid #CC333F;
  color: white;
  box-shadow: 0px 1px 2px rgba(8, 11, 13, 0.05);
  border-radius: 6px;
  cursor: pointer;
}

.delete-btn:hover:not(:disabled) {
  background: #B82E39;
  border-color: #B82E39;
}

.delete-btn:disabled {
  background: #D1D1D3;
  border-color: #D1D1D3;
  color: #909092;
  cursor: not-allowed;
  opacity: 0.6;
}

/deep/ #deleteAccountModal .modal-header {
  padding: 24px;
  border-bottom: 1px solid #E5E5E6;
}

/deep/ #deleteAccountModal .modal-body {
  padding: 24px;
}

/deep/ #deleteAccountModal .modal-footer {
  padding: 16px 24px;
  border-top: 1px solid #E5E5E6;
}
</style>
